/* eslint-disable */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import '../../styles/components.css';
import { useAuth } from '../../contexts/AuthContext';

const ProjectReview: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { userProfile } = useAuth();
  const companyId = userProfile?.company_id;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [project, setProject] = useState<any>(null);
  const [projectNGO, setProjectNGO] = useState<any>(null);
  const isEditMode = new URLSearchParams(window.location.search).get('edit') === 'true';

  // Premium CSS Styles
  const inputStyle = {
    padding: '0.625rem 0.875rem',
    borderRadius: '8px',
    border: '1px solid #d1d5db',
    fontSize: '0.875rem',
    transition: 'all 0.2s ease',
    width: '100%',
    outline: 'none',
    backgroundColor: '#ffffff'
  };

  const readOnlyBoxStyle = {
    padding: '0.625rem 0.875rem',
    background: '#f9fafb',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    fontSize: '0.925rem',
    fontWeight: 500,
    color: '#4b5563',
    display: 'center' as const, // Fixed to valid CSS property if needed, but display: 'center' is not standard. Changed to flex center.
    alignItems: 'center'
  };

  const labelStyle = {
    display: 'block',
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.025em',
    color: '#6b7280',
    marginBottom: '0.5rem'
  };

  const [assignedNGOs, setAssignedNGOs] = useState<any[]>([]);
  const [requestChangesComment, setRequestChangesComment] = useState('');
  const [projectMetrics, setProjectMetrics] = useState<any[]>([]);
  const [editData, setEditData] = useState<any>({
    start_date: '',
    end_date: '',
    reporting_cadence: 'monthly',
    thematic_area: '',
    locations: [],
    beneficiary_profile: '',
    proposal_content: {
      problem_statement: '',
      strategy_approach: '',
      planned_activities: '',
      expected_outcomes: ''
    },
    metrics: [],
    tranche_plan: [],
    budget: ''
  });

  useEffect(() => {
    if (projectId) {
      loadProject();
      loadAssignedNGOs();

    }
  }, [projectId]);

  const loadProject = async () => {
    if (!projectId) return;

    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .eq('id', projectId)
      .single();

    if (!error && data) {
      setProject(data);

      let proposalContent = {
        problem_statement: '',
        strategy_approach: '',
        planned_activities: '',
        expected_outcomes: ''
      };

      try {
        if (data.proposal_content) {
          const parsed = typeof data.proposal_content === 'string'
            ? JSON.parse(data.proposal_content)
            : data.proposal_content;
          proposalContent = { ...proposalContent, ...parsed };
        }
      } catch (e) {
        console.error("Error parsing proposal content", e);
      }

      setEditData((prev: any) => ({
        ...prev,
        start_date: data.start_date || '',
        end_date: data.end_date || '',
        reporting_cadence: data.reporting_cadence || 'monthly',
        thematic_area: data.thematic_area || '',
        locations: Array.isArray(data.locations) ? data.locations : [],
        beneficiary_profile: data.beneficiary_profile || '',
        proposal_content: proposalContent,
        tranche_plan: Array.isArray(data.tranche_plan) ? data.tranche_plan : [],
        budget: data.budget || ''
      }));

      // Also fetch metrics from project_metrics_progress table
      const { data: metricsData, error: metricsError } = await supabase
        .from('project_metrics_progress')
        .select('*')
        .eq('project_id', projectId);

      if (!metricsError && metricsData) {
        setProjectMetrics(metricsData);
        setEditData((prev: any) => ({
          ...prev,
          metrics: metricsData.map((m: any) => ({
            name: m.metric_name,
            target: m.target_value,
            unit: m.unit || ''
          }))
        }));
      }
    }
    if (loading) {
      setLoading(false);
    }
  };

  const getSignedUrl = async (fileUrl: string): Promise<string> => {
    try {
      if (!fileUrl) return '';
      if (!fileUrl.startsWith('http')) return fileUrl;

      const bucketName = 'ngo-documents';
      const urlParts = fileUrl.split(`${bucketName}/`);
      if (urlParts.length < 2) return fileUrl;

      const filePath = decodeURIComponent(urlParts[1].split('?')[0]);
      const { data, error } = await supabase.storage
        .from(bucketName)
        .createSignedUrl(filePath, 3600);

      return data?.signedUrl || fileUrl;
    } catch (err) {
      console.error('Error getting signed URL:', err);
      return fileUrl;
    }
  };

  // Refresh project data when assignedNGOs changes (to catch updates)
  useEffect(() => {
    if (projectId && assignedNGOs.length > 0) {
      loadProject();
    }
  }, [assignedNGOs.length]);

  const loadAssignedNGOs = async () => {
    if (!projectId) return;

    const { data, error } = await supabase
      .from('project_ngos')
      .select(`
        ngo_id,
        status,
        requested_changes,
        ngos (
          id,
          legal_name,
          contact
        )
      `)
      .eq('project_id', projectId);

    if (!error && data) {
      setAssignedNGOs(data);
    }
  };

  const handleAccept = async () => {
    setSaving(true);
    setError('');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !projectId) throw new Error('Not authenticated');

      // --- Synchronize Proposal Content Budget ---
      const finalProposalContent = {
        ...(typeof editData.proposal_content === 'object' ? editData.proposal_content : {}),
        proposed_budget: Number(editData.budget) || 0
      };

      // SAVE UPDATED DETAILS BEFORE ACTIVATING
      const { error: projectSaveError } = await supabase
        .from('projects')
        .update({
          start_date: editData.start_date || null,
          end_date: editData.end_date || null,
          reporting_cadence: editData.reporting_cadence,
          thematic_area: editData.thematic_area,
          locations: editData.locations,
          beneficiary_profile: editData.beneficiary_profile,
          proposal_content: finalProposalContent,
          tranche_plan: editData.tranche_plan,
          budget: Number(editData.budget) || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', projectId);

      if (projectSaveError) throw projectSaveError;

      // --- Sync Metrics across both tables ---
      const currentMetricNames = editData.metrics.map((m: any) => m.name.trim()).filter(Boolean);

      // Delete removed metrics from BOTH tables
      // 1. Get existing metrics to identify deletions
      const { data: existingMetrics } = await supabase
        .from('project_metrics_progress')
        .select('metric_name')
        .eq('project_id', projectId)
        .is('reporting_period', null);

      const metricsToDelete = (existingMetrics || [])
        .map((m: any) => m.metric_name)
        .filter((name: string) => !currentMetricNames.includes(name));

      for (const nameToDelete of metricsToDelete) {
        await supabase.from('project_metrics').delete().eq('project_id', projectId).eq('metric_name', nameToDelete);
        await supabase.from('project_metrics_progress').delete().eq('project_id', projectId).eq('metric_name', nameToDelete);
      }

      // Update/Insert metrics
      for (const metric of editData.metrics) {
        const metricName = metric.name.trim();
        const targetValue = Number(metric.target) || 0;
        const unit = metric.unit || '';

        if (!metricName) continue;

        // 1. Sync with project_metrics (Definition table)
        const { data: mDef } = await supabase.from('project_metrics').select('id').eq('project_id', projectId).eq('metric_name', metricName).maybeSingle();
        if (mDef) {
          await supabase.from('project_metrics').update({ target_value: targetValue, unit: unit }).eq('id', mDef.id);
        } else {
          await supabase.from('project_metrics').insert({ project_id: projectId, metric_name: metricName, target_value: targetValue, unit: unit, baseline_value: 0 });
        }

        // 2. Sync with project_metrics_progress (Tracking table)
        const { data: existingMetric } = await supabase
          .from('project_metrics_progress')
          .select('id')
          .eq('project_id', projectId)
          .eq('metric_name', metricName)
          .is('reporting_period', null)
          .maybeSingle();

        if (existingMetric) {
          await supabase
            .from('project_metrics_progress')
            .update({
              target_value: targetValue,
              unit: unit,
              updated_at: new Date().toISOString()
            })
            .eq('id', existingMetric.id);
        } else {
          await supabase
            .from('project_metrics_progress')
            .insert({
              project_id: projectId,
              metric_name: metricName,
              target_value: targetValue,
              unit: unit,
              current_value: 0,
              updated_at: new Date().toISOString()
            });
        }
      }

      // Update project status to 'active' (Approved by company)
      const { error: updateError } = await supabase
        .from('projects')
        .update({
          status: 'active',
          updated_at: new Date().toISOString()
        })
        .eq('id', projectId);

      if (updateError) throw updateError;

      // Update all assigned NGOs to 'accepted' status
      for (const assignedNGO of assignedNGOs) {
        await supabase
          .from('project_ngos')
          .update({
            status: 'accepted',
            accepted_at: new Date().toISOString()
          })
          .eq('project_id', projectId)
          .eq('ngo_id', assignedNGO.ngo_id);
      }

      alert('Project activated successfully! The project is now live and budget has been assigned.');
      navigate(`/client/company/projects`);
    } catch (err: any) {
      setError(err.message || 'Failed to accept project');
    } finally {
      setSaving(false);
    }
  };

  const handleRequestChanges = async () => {
    if (!requestChangesComment.trim()) {
      setError('Please provide a comment explaining the requested changes');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !projectId) throw new Error('Not authenticated');

      // Update project status to 'draft' and store change request
      const { error: updateError } = await supabase
        .from('projects')
        .update({
          status: 'draft',
          changes_requested: {
            requested_by: user.id,
            comment: requestChangesComment,
            requested_at: new Date().toISOString()
          },
          updated_at: new Date().toISOString()
        })
        .eq('id', projectId);

      if (updateError) throw updateError;

      // Update all assigned NGOs to 'changes_requested' status
      for (const assignedNGO of assignedNGOs) {
        await supabase
          .from('project_ngos')
          .update({
            status: 'changes_requested',
            requested_changes: requestChangesComment
          })
          .eq('project_id', projectId)
          .eq('ngo_id', assignedNGO.ngo_id);
      }

      alert('Changes requested. The project has been reverted to draft status with your comments visible to the NGO.');
      navigate(`/client/company/projects`);
    } catch (err: any) {
      setError(err.message || 'Failed to request changes');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDetails = async () => {
    setSaving(true);
    setError('');

    try {
      // --- Synchronize Proposal Content Budget ---
      const finalProposalContent = {
        ...(typeof editData.proposal_content === 'object' ? editData.proposal_content : {}),
        proposed_budget: Number(editData.budget) || 0
      };

      const { error: updateError } = await supabase
        .from('projects')
        .update({
          start_date: editData.start_date || null,
          end_date: editData.end_date || null,
          reporting_cadence: editData.reporting_cadence,
          thematic_area: editData.thematic_area,
          locations: editData.locations,
          beneficiary_profile: editData.beneficiary_profile,
          proposal_content: finalProposalContent,
          tranche_plan: editData.tranche_plan,
          budget: Number(editData.budget) || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', projectId);

      if (updateError) throw updateError;

      // --- Sync Metrics across both tables ---
      const currentMetricNames = editData.metrics.map((m: any) => m.name.trim()).filter(Boolean);

      // Delete removed metrics from BOTH tables
      const { data: existingMetrics } = await supabase
        .from('project_metrics_progress')
        .select('metric_name')
        .eq('project_id', projectId)
        .is('reporting_period', null);

      const metricsToDelete = (existingMetrics || [])
        .map((m: any) => m.metric_name)
        .filter((name: string) => !currentMetricNames.includes(name));

      for (const nameToDelete of metricsToDelete) {
        await supabase.from('project_metrics').delete().eq('project_id', projectId).eq('metric_name', nameToDelete);
        await supabase.from('project_metrics_progress').delete().eq('project_id', projectId).eq('metric_name', nameToDelete);
      }

      // Update/Insert metrics
      for (const metric of editData.metrics) {
        const metricName = metric.name.trim();
        const targetValue = Number(metric.target) || 0;
        const unit = metric.unit || '';

        if (!metricName) continue;

        // 1. Sync with project_metrics (Definition table)
        const { data: mDef } = await supabase.from('project_metrics').select('id').eq('project_id', projectId).eq('metric_name', metricName).maybeSingle();
        if (mDef) {
          await supabase.from('project_metrics').update({ target_value: targetValue, unit: unit }).eq('id', mDef.id);
        } else {
          await supabase.from('project_metrics').insert({ project_id: projectId, metric_name: metricName, target_value: targetValue, unit: unit, baseline_value: 0 });
        }

        // 2. Sync with project_metrics_progress (Tracking table)
        const { data: existingMetric } = await supabase
          .from('project_metrics_progress')
          .select('id')
          .eq('project_id', projectId)
          .eq('metric_name', metricName)
          .is('reporting_period', null)
          .maybeSingle();

        if (existingMetric) {
          await supabase
            .from('project_metrics_progress')
            .update({
              target_value: targetValue,
              unit: unit,
              updated_at: new Date().toISOString()
            })
            .eq('id', existingMetric.id);
        } else {
          await supabase
            .from('project_metrics_progress')
            .insert({
              project_id: projectId,
              metric_name: metricName,
              target_value: targetValue,
              unit: unit,
              current_value: 0,
              updated_at: new Date().toISOString()
            });
        }
      }

      await loadProject();
      alert('Project details updated successfully');
    } catch (err: any) {
      setError(err.message || 'Failed to update details');
    } finally {
      setSaving(false);
    }
  };

  const addLocation = () => {
    setEditData({
      ...editData,
      locations: [...editData.locations, { name: '', state: '', district: '' }]
    });
  };

  const removeLocation = (index: number) => {
    setEditData({
      ...editData,
      locations: editData.locations.filter((_: any, i: number) => i !== index)
    });
  };

  const addMetric = () => {
    setEditData({
      ...editData,
      metrics: [...editData.metrics, { name: '', target: '', unit: '' }]
    });
  };

  const removeMetric = (index: number) => {
    setEditData({
      ...editData,
      metrics: editData.metrics.filter((_: any, i: number) => i !== index)
    });
  };

  const addTranche = () => {
    const nextNum = editData.tranche_plan.length + 1;
    setEditData({
      ...editData,
      tranche_plan: [...editData.tranche_plan, { tranche_number: nextNum, amount: '', planned_date: '' }]
    });
  };

  const removeTranche = (index: number) => {
    setEditData({
      ...editData,
      tranche_plan: editData.tranche_plan.filter((_: any, i: number) => i !== index)
        .map((t: any, idx: number) => ({ ...t, tranche_number: idx + 1 }))
    });
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="form-container">
        <div className="form-section">
          <h1>Project Not Found</h1>
          <p>This project could not be loaded.</p>
        </div>
      </div>
    );
  }

  // Check if project has details filled by NGO
  const hasProjectDetails = project.thematic_area &&
    (project.start_date || projectMetrics.length > 0 || project.budget || project.locations?.length > 0);

  // Also check if any NGO has submitted (status: 'pending' in project_ngos means NGO filled and submitted)
  const hasNgoSubmission = assignedNGOs.some((pn: any) => pn.status === 'pending');

  // Show details if:
  // 1. Project status is 'draft' (NGO submitted), OR
  // 2. Project has details filled, OR
  // 3. Any NGO has status 'pending' (submitted for review), OR
  // 4. Project is direct implementation
  const shouldShowDetails = project.status === 'draft' || hasProjectDetails || hasNgoSubmission || project.implementation_type === 'direct';

  const changesRequested = project.changes_requested;

  return (
    <div className="form-container">
      <div className="form-section">
        <div className="project-review-header">
          <h1>Project Review</h1>
          <div className="project-review-header-actions">
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => {
                loadProject();
                loadAssignedNGOs();
              }}
              style={{ fontSize: '0.875rem' }}
            >
              Refresh
            </button>
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => navigate(`/client/company/projects`)}
            >
              Back to Projects
            </button>
          </div>
        </div>

        {error && (
          <div className="toast toast-error" style={{ position: 'relative', marginBottom: '1rem' }}>
            {error}
          </div>
        )}

        {changesRequested && (
          <div className="card" style={{ background: '#fef3c7', border: '1px solid #f59e0b', marginBottom: '1.5rem' }}>
            <h3 style={{ marginBottom: '0.5rem', color: '#92400e' }}>Previous Change Request</h3>
            <p style={{ margin: 0, color: '#92400e' }}>
              {typeof changesRequested === 'object' ? changesRequested.comment : changesRequested}
            </p>
            {typeof changesRequested === 'object' && changesRequested.requested_at && (
              <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem', color: '#92400e' }}>
                Requested on: {new Date(changesRequested.requested_at).toLocaleString()}
              </p>
            )}
          </div>
        )}

        {/* Project Basic Info */}
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <h2>{project.name}</h2>
          <p style={{ color: 'var(--muted)', marginBottom: '1rem' }}>{project.summary}</p>

          <div className="form-row">
            <div>
              <strong>Status:</strong>
              <p>{project.status.charAt(0).toUpperCase() + project.status.slice(1)}</p>
            </div>
            <div>
              <strong>Created:</strong>
              <p>{new Date(project.created_at).toLocaleDateString()}</p>
            </div>
          </div>
        </div>

        {/* Assigned NGOs */}
        {assignedNGOs.length > 0 && (
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <h2>Assigned NGOs</h2>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>NGO Name</th>
                    <th>Status</th>
                    <th>Contact</th>
                  </tr>
                </thead>
                <tbody>
                  {assignedNGOs.map((pn: any) => (
                    <tr key={pn.ngo_id}>
                      <td>{pn.ngos?.legal_name || 'NGO'}</td>
                      <td>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '12px',
                          fontSize: '0.875rem',
                          backgroundColor: pn.status === 'accepted' ? '#10b98115' :
                            pn.status === 'changes_requested' ? '#ef444415' :
                              pn.status === 'pending' ? '#3b82f615' : '#f59e0b15',
                          color: pn.status === 'accepted' ? '#10b981' :
                            pn.status === 'changes_requested' ? '#ef4444' :
                              pn.status === 'pending' ? '#3b82f6' : '#f59e0b'
                        }}>
                          {pn.status === 'pending' ? 'Submitted for Review' : pn.status.charAt(0).toUpperCase() + pn.status.slice(1).replace('_', ' ')}
                        </span>
                      </td>
                      <td>{pn.ngos?.contact?.email || 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {project.implementation_type === 'direct' && (
          <div className="card" style={{ background: 'var(--primary-light)', border: '1px solid var(--primary)', marginBottom: '1.5rem' }}>
            <h3 style={{ marginBottom: '0.5rem', color: 'var(--primary)' }}>Direct Implementation</h3>
            <p style={{ margin: 0, color: 'var(--text)' }}>
              This project is being implemented directly by your company. You can manage all project details, budget, and metrics from this dashboard.
            </p>
          </div>
        )}

        {!shouldShowDetails ? (
          <div className="card" style={{ background: '#eff6ff', border: '1px solid #3b82f6', marginBottom: '1.5rem' }}>
            <h3 style={{ marginBottom: '0.5rem', color: '#1e40af' }}>Waiting for NGO Details</h3>
            <p style={{ margin: 0, color: '#1e40af' }}>
              This project has been assigned to NGOs. The NGOs need to fill in all project details including thematic area, dates, locations, metrics, budget, and proposal content.
            </p>
          </div>
        ) : (
          <>
            {/* Project Details (Thematic, Locations, etc.) */}
            <div className="card" style={{ marginBottom: '1.5rem' }}>
              <h2>Project Details</h2>

              <div className="form-row">
                <div>
                  <label style={labelStyle}>Thematic Area</label>
                  {isEditMode || project.implementation_type === 'direct' ? (
                    <input 
                      value={editData.thematic_area} 
                      onChange={(e) => setEditData({...editData, thematic_area: e.target.value})}
                      style={inputStyle}
                      placeholder="e.g. Education, Health"
                    />
                  ) : (
                    <p>{project.thematic_area || 'Not specified'}</p>
                  )}
                </div>
                <div>
                  <label style={labelStyle}>Reporting Cadence</label>
                  {isEditMode || project.implementation_type === 'direct' ? (
                    <select 
                      value={editData.reporting_cadence} 
                      onChange={(e) => setEditData({...editData, reporting_cadence: e.target.value})}
                      style={inputStyle}
                    >
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="half-yearly">Half-Yearly</option>
                      <option value="yearly">Yearly</option>
                    </select>
                  ) : (
                    <p>{project.reporting_cadence || 'Not specified'}</p>
                  )}
                </div>
              </div>

              <div className="form-row" style={{ marginTop: '1.5rem' }}>
                <div>
                   <label style={labelStyle}>Start Date</label>
                   {isEditMode || project.implementation_type === 'direct' ? (
                      <input 
                        type="date" 
                        value={editData.start_date ? editData.start_date.split('T')[0] : ''} 
                        onChange={(e) => setEditData({...editData, start_date: e.target.value})}
                        style={inputStyle}
                      />
                   ) : (
                      <p>{project.start_date ? new Date(project.start_date).toLocaleDateString() : 'N/A'}</p>
                   )}
                </div>
                <div>
                   <label style={labelStyle}>End Date</label>
                   {isEditMode || project.implementation_type === 'direct' ? (
                      <input 
                        type="date" 
                        value={editData.end_date ? editData.end_date.split('T')[0] : ''} 
                        onChange={(e) => setEditData({...editData, end_date: e.target.value})}
                        style={inputStyle}
                      />
                   ) : (
                      <p>{project.end_date ? new Date(project.end_date).toLocaleDateString() : 'N/A'}</p>
                   )}
                </div>
              </div>

              <div style={{ marginTop: '1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <label style={labelStyle}>Locations</label>
                  {(isEditMode || project.implementation_type === 'direct') && (
                    <button onClick={addLocation} className="btn btn-sm btn-outline" style={{ borderRadius: '20px', padding: '0.2rem 0.8rem', fontSize: '0.7rem' }}>+ Add Location</button>
                  )}
                </div>
                {isEditMode || project.implementation_type === 'direct' ? (
                  <div style={{ display: 'grid', gap: '0.75rem' }}>
                    {editData.locations.map((loc: any, idx: number) => (
                      <div key={idx} className="location-item-grid">
                        <div>
                          <label style={{ fontSize: '0.65rem', color: '#6b7280' }}>Location Name</label>
                          <input 
                            value={loc.name || ''} 
                            onChange={(e) => {
                              const newLocs = [...editData.locations];
                              newLocs[idx].name = e.target.value;
                              setEditData({...editData, locations: newLocs});
                            }}
                            style={inputStyle}
                            placeholder="e.g. Village Name"
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: '0.65rem', color: '#6b7280' }}>State</label>
                          <input 
                            value={loc.state || ''} 
                            onChange={(e) => {
                              const newLocs = [...editData.locations];
                              newLocs[idx].state = e.target.value;
                              setEditData({...editData, locations: newLocs});
                            }}
                            style={inputStyle}
                            placeholder="State"
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: '0.65rem', color: '#6b7280' }}>District</label>
                          <input 
                            value={loc.district || ''} 
                            onChange={(e) => {
                              const newLocs = [...editData.locations];
                              newLocs[idx].district = e.target.value;
                              setEditData({...editData, locations: newLocs});
                            }}
                            style={inputStyle}
                            placeholder="District"
                          />
                        </div>
                        <button 
                          onClick={() => removeLocation(idx)} 
                          className="btn btn-sm btn-danger" 
                          style={{ 
                            height: '38px', 
                            minWidth: '38px', 
                            padding: 0,
                            borderRadius: '50%',
                            width: '38px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: '#fee2e2',
                            color: '#ef4444',
                            border: '1px solid #fecaca'
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  project.locations && Array.isArray(project.locations) && project.locations.length > 0 ? (
                    <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>
                      {project.locations.map((loc: any, idx: number) => (
                        <li key={idx}>
                          {loc.name || 'Location'} - {loc.state || ''} {loc.district ? `(${loc.district})` : ''}
                        </li>
                      ))}
                    </ul>
                  ) : <p>No locations specified</p>
                )}
              </div>

              <div style={{ marginTop: '1.5rem' }}>
                <label style={labelStyle}>Beneficiary Profile</label>
                {isEditMode || project.implementation_type === 'direct' ? (
                  <textarea 
                    value={editData.beneficiary_profile || ''} 
                    onChange={(e) => setEditData({...editData, beneficiary_profile: e.target.value})}
                    style={{...inputStyle, height: '80px', resize: 'vertical'}}
                    placeholder="e.g. Marginalized rural children aged 6-14"
                  />
                ) : (
                  <p>{project.beneficiary_profile || 'Not specified'}</p>
                )}
              </div>
            </div>

            {/* Proposal Content - Moved UP as per sequence */}
            {project.proposal_content && (
              <div className="card" style={{ marginBottom: '1.5rem' }}>
                <h2>Proposal Content</h2>
                {(() => {
                  let proposalData: any = {};
                  try {
                    proposalData = typeof project.proposal_content === 'string'
                      ? JSON.parse(project.proposal_content)
                      : project.proposal_content;
                  } catch {
                    proposalData = { full_text: project.proposal_content };
                  }

                  return (
                    <>
                      <div style={{ marginBottom: '1.5rem' }}>
                        <label style={labelStyle}>Problem Statement</label>
                        {isEditMode || project.implementation_type === 'direct' ? (
                          <textarea 
                            value={editData.proposal_content.problem_statement || ''} 
                            onChange={(e) => setEditData({...editData, proposal_content: {...editData.proposal_content, problem_statement: e.target.value}})}
                            style={{...inputStyle, height: '120px', resize: 'vertical'}}
                            placeholder="Describe the problem this project addresses..."
                          />
                        ) : (
                          <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6', padding: '1rem', background: '#f9fafb', borderRadius: '8px' }}>
                            {proposalData.problem_statement}
                          </div>
                        )}
                      </div>
                      
                      <div style={{ marginBottom: '1.5rem' }}>
                        <label style={labelStyle}>Strategy / Approach</label>
                        {isEditMode || project.implementation_type === 'direct' ? (
                          <textarea 
                            value={editData.proposal_content.strategy_approach || ''} 
                            onChange={(e) => setEditData({...editData, proposal_content: {...editData.proposal_content, strategy_approach: e.target.value}})}
                            style={{...inputStyle, height: '120px', resize: 'vertical'}}
                            placeholder="Describe your implementation strategy..."
                          />
                        ) : (
                          <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6', padding: '1rem', background: '#f9fafb', borderRadius: '8px' }}>
                            {proposalData.strategy_approach}
                          </div>
                        )}
                      </div>

                      <div style={{ marginBottom: '1.5rem' }}>
                        <label style={labelStyle}>Planned Activities</label>
                        {isEditMode || project.implementation_type === 'direct' ? (
                          <textarea 
                            value={editData.proposal_content.planned_activities || ''} 
                            onChange={(e) => setEditData({...editData, proposal_content: {...editData.proposal_content, planned_activities: e.target.value}})}
                            style={{...inputStyle, height: '120px', resize: 'vertical'}}
                            placeholder="List the key activities..."
                          />
                        ) : (
                          <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6', padding: '1rem', background: '#f9fafb', borderRadius: '8px' }}>
                            {proposalData.planned_activities}
                          </div>
                        )}
                      </div>

                      <div style={{ marginBottom: '1.5rem' }}>
                        <label style={labelStyle}>Expected Outcomes</label>
                        {isEditMode || project.implementation_type === 'direct' ? (
                          <textarea 
                            value={editData.proposal_content.expected_outcomes || ''} 
                            onChange={(e) => setEditData({...editData, proposal_content: {...editData.proposal_content, expected_outcomes: e.target.value}})}
                            style={{...inputStyle, height: '120px', resize: 'vertical'}}
                            placeholder="What are the expected results?"
                          />
                        ) : (
                          <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6', padding: '1rem', background: '#f9fafb', borderRadius: '8px' }}>
                            {proposalData.expected_outcomes}
                          </div>
                        )}
                      </div>

                      {proposalData.proposal_pdf_url && (
                        <div className="proposal-pdf-box">
                          <div>
                            <h3 style={{ margin: '0 0 0.25rem 0', color: '#9a3412' }}>Full Detailed Proposal (PDF)</h3>
                            <p style={{ margin: 0, fontSize: '0.875rem', color: '#c2410c' }}>
                              The NGO has uploaded a comprehensive proposal document for your review.
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={async () => {
                              const url = await getSignedUrl(proposalData.proposal_pdf_url);
                              window.open(url, '_blank');
                            }}
                            className="btn btn-primary"
                            style={{
                              background: '#f97316',
                              border: 'none',
                              padding: '0.75rem 1.5rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            <span>📄</span> Download Proposal
                          </button>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}

            {/* Metrics & Targets - Editable in Review */}
            {(projectMetrics.length > 0 || (project.status === 'draft' || hasNgoSubmission)) && (
              <div className="card" style={{ marginBottom: '1.5rem', overflow: 'visible' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                  <h2 style={{ margin: 0 }}>Metrics & Targets</h2>
                  {(project.status === 'draft' || hasNgoSubmission) && (
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={addMetric}
                      style={{ borderRadius: '20px', padding: '0.4rem 1rem' }}
                    >
                      + Add New Metric
                    </button>
                  )}
                </div>
                <div className="table-container" style={{ border: '1px solid #e5e7eb', borderRadius: '12px', overflow: 'hidden' }}>
                  <table className="table" style={{ margin: 0 }}>
                    <thead style={{ background: '#f9fafb' }}>
                      <tr>
                        <th style={{ padding: '1rem' }}>Metric Name</th>
                        <th style={{ padding: '1rem' }}>Target</th>
                        <th style={{ padding: '1rem' }}>Unit</th>
                        <th style={{ padding: '1rem' }}>Current</th>
                        {(project.status === 'draft' || hasNgoSubmission) && <th style={{ padding: '1rem' }}>Action</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {(project.status === 'draft' || hasNgoSubmission || project.implementation_type === 'direct') ? (
                        editData.metrics.map((metric: any, index: number) => (
                          <tr key={index}>
                            <td style={{ padding: '0.75rem' }}>
                              <input
                                value={metric.name || ''}
                                onChange={e => {
                                  const newMetrics = [...editData.metrics];
                                  newMetrics[index].name = e.target.value;
                                  setEditData({ ...editData, metrics: newMetrics });
                                }}
                                placeholder="e.g. Students Impacted"
                                style={{ ...inputStyle }}
                                onFocus={(e: any) => e.target.style.borderColor = 'var(--primary)'}
                                onBlur={(e: any) => e.target.style.borderColor = '#d1d5db'}
                              />
                            </td>
                            <td style={{ padding: '0.75rem' }}>
                              <input
                                type="number"
                                value={metric.target || ''}
                                onChange={e => {
                                  const newMetrics = [...editData.metrics];
                                  newMetrics[index].target = e.target.value;
                                  setEditData({ ...editData, metrics: newMetrics });
                                }}
                                placeholder="0"
                                style={{ ...inputStyle, width: '100px' }}
                                onFocus={(e: any) => e.target.style.borderColor = 'var(--primary)'}
                                onBlur={(e: any) => e.target.style.borderColor = '#d1d5db'}
                              />
                            </td>
                            <td style={{ padding: '0.75rem' }}>
                              <input
                                value={metric.unit || ''}
                                onChange={e => {
                                  const newMetrics = [...editData.metrics];
                                  newMetrics[index].unit = e.target.value;
                                  setEditData({ ...editData, metrics: newMetrics });
                                }}
                                placeholder="Unit"
                                style={{ ...inputStyle, width: '80px' }}
                                onFocus={(e: any) => e.target.style.borderColor = 'var(--primary)'}
                                onBlur={(e: any) => e.target.style.borderColor = '#d1d5db'}
                              />
                            </td>
                            <td style={{ padding: '0.75rem', color: '#6b7280' }}>
                              {projectMetrics.find(pm => pm.metric_name === metric.name)?.current_value || 0}
                            </td>
                            <td style={{ padding: '0.75rem' }}>
                                <button
                                  className="btn btn-danger btn-sm"
                                  onClick={() => removeMetric(index)}
                                  style={{
                                    borderRadius: '50%',
                                    width: '24px',
                                    height: '24px',
                                    padding: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '0.875rem',
                                    lineHeight: 1,
                                    minWidth: '24px',
                                    background: '#fee2e2',
                                    color: '#ef4444',
                                    border: '1px solid #fecaca'
                                  }}
                                  title="Remove"
                                >
                                  ×
                                </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        projectMetrics.map((m: any, index: number) => (
                          <tr key={index}>
                            <td style={{ padding: '1rem' }}>{m.metric_name}</td>
                            <td style={{ padding: '1rem' }}>{m.target_value}</td>
                            <td style={{ padding: '1rem' }}>{m.unit || ''}</td>
                            <td style={{ padding: '1rem' }}>{m.current_value || 0}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Budget & Funding Schedule - Editable in Review */}
            {(project.budget || (project.status === 'draft' || hasNgoSubmission || project.implementation_type === 'direct')) && (
              <div className="card" style={{ marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                  <h2 style={{ margin: 0 }}>Budget & Funding Schedule</h2>
                  {(project.status === 'draft' || hasNgoSubmission || project.implementation_type === 'direct') && (
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={addTranche}
                      style={{ borderRadius: '20px', padding: '0.4rem 1rem' }}
                    >
                      + Add New Installment
                    </button>
                  )}
                </div>

                {(project.status === 'draft' || hasNgoSubmission || project.implementation_type === 'direct') ? (
                  <div style={{ marginBottom: '1.5rem' }}>
                    <div style={{
                      background: '#f5f1f0',
                      padding: '1.5rem',
                      borderRadius: '12px',
                      border: '1px solid #e2e8f0',
                      marginBottom: '2rem',
                      maxWidth: '400px'
                    }}>
                      <label style={{ ...labelStyle, color: 'var(--primary)' }}>Final Assigned Budget *</label>
                      <div style={{ position: 'relative' }}>
                        <span style={{ position: 'absolute', left: '0.875rem', top: '50%', transform: 'translateY(-50%)', color: '#6b7280' }}>₹</span>
                        <input
                          type="number"
                          value={editData.budget || ''}
                          onChange={e => setEditData({ ...editData, budget: e.target.value })}
                          placeholder="Enter final amount"
                          style={{ ...inputStyle, paddingLeft: '2rem', border: '2px solid var(--primary)' }}
                        />
                      </div>
                    </div>

                    <h3 style={{ fontSize: '1rem', marginBottom: '1rem', color: '#374151' }}>Funding Schedule</h3>
                    <div className="table-container" style={{ border: '1px solid #e5e7eb', borderRadius: '12px', overflow: 'hidden' }}>
                      <table className="table" style={{ margin: 0 }}>
                        <thead style={{ background: '#f9fafb' }}>
                          <tr>
                            <th style={{ padding: '1rem' }}>Installment</th>
                            <th style={{ padding: '1rem' }}>Amount (₹)</th>
                            <th style={{ padding: '1rem' }}>Planned Date</th>
                            <th style={{ padding: '1rem' }}>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {editData.tranche_plan.map((tranche: any, index: number) => (
                            <tr key={index}>
                              <td style={{ padding: '0.75rem', fontWeight: 500, color: '#4b5563' }}>
                                Installment {tranche.tranche_number || index + 1}
                              </td>
                              <td style={{ padding: '0.75rem' }}>
                                <div style={{ position: 'relative' }}>
                                  <span style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: '0.875rem' }}>₹</span>
                                  <input
                                    type="number"
                                    value={tranche.amount || ''}
                                    onChange={e => {
                                      const newTranches = [...editData.tranche_plan];
                                      newTranches[index].amount = e.target.value;
                                      setEditData({ ...editData, tranche_plan: newTranches });
                                    }}
                                    style={{ ...inputStyle, paddingLeft: '1.75rem' }}
                                    onFocus={(e: any) => e.target.style.borderColor = 'var(--primary)'}
                                    onBlur={(e: any) => e.target.style.borderColor = '#d1d5db'}
                                  />
                                </div>
                              </td>
                              <td style={{ padding: '0.75rem' }}>
                                <input
                                  type="date"
                                  value={tranche.planned_date || ''}
                                  onChange={e => {
                                    const newTranches = [...editData.tranche_plan];
                                    newTranches[index].planned_date = e.target.value;
                                    setEditData({ ...editData, tranche_plan: newTranches });
                                  }}
                                  style={{ ...inputStyle }}
                                  onFocus={(e: any) => e.target.style.borderColor = 'var(--primary)'}
                                  onBlur={(e: any) => e.target.style.borderColor = '#d1d5db'}
                                />
                              </td>
                              <td style={{ padding: '0.75rem' }}>
                                <button
                                  className="btn btn-danger btn-sm"
                                  onClick={() => removeTranche(index)}
                                  style={{
                                    borderRadius: '50%',
                                    width: '24px',
                                    height: '24px',
                                    padding: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '0.875rem',
                                    lineHeight: 1,
                                    minWidth: '24px',
                                    background: '#fee2e2',
                                    color: '#ef4444',
                                    border: '1px solid #fecaca'
                                  }}
                                  title="Remove"
                                >
                                  ×
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={{ marginBottom: '1rem' }}>
                      <strong>Total Budget:</strong>
                      <p>₹{parseFloat(project.budget).toLocaleString('en-IN')}</p>
                    </div>

                    {project.tranche_plan && Array.isArray(project.tranche_plan) && project.tranche_plan.length > 0 && (
                      <div>
                        <strong>Funding Schedule:</strong>
                        <div className="table-container" style={{ marginTop: '0.5rem' }}>
                          <table className="table">
                            <thead>
                              <tr>
                                <th>Installment</th>
                                <th>Amount</th>
                                <th>Planned Date</th>
                              </tr>
                            </thead>
                            <tbody>
                              {project.tranche_plan.map((tranche: any, index: number) => (
                                <tr key={index}>
                                  <td>Installment {tranche.tranche_number || index + 1}</td>
                                  <td>₹{tranche.amount ? parseFloat(tranche.amount).toLocaleString('en-IN') : 'N/A'}</td>
                                  <td>{tranche.planned_date ? new Date(tranche.planned_date).toLocaleDateString() : 'N/A'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </>
        )}



        {/* Action Buttons */}
        {shouldShowDetails && (project.status === 'draft' || hasNgoSubmission || project.implementation_type === 'direct') && project.status !== 'closed' && (
          <div className="card" style={{ marginTop: '2rem' }}>
            <h2>Review & Approval</h2>
            <p style={{ color: 'var(--muted)', marginBottom: '1.5rem', fontSize: '0.875rem' }}>
              Review all project details above. You can either approve the project or request changes from the NGO.
            </p>

            <div className="form-group">
              <label>Comments (Required if requesting changes)</label>
              <textarea
                value={requestChangesComment}
                onChange={(e) => setRequestChangesComment(e.target.value)}
                placeholder="Explain what changes you need from the NGO..."
                rows={4}
                style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '0.875rem' }}
              />
            </div>

            <div className="form-actions" style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleRequestChanges}
                disabled={saving || !requestChangesComment.trim()}
                style={{ flex: 1 }}
              >
                {saving ? 'Processing...' : 'Request Changes'}
              </button>
              <button
                type="button"
                className="btn btn-success"
                onClick={handleAccept}
                disabled={saving}
                style={{ flex: 1, background: '#10b981', color: 'white' }}
              >
                {saving ? 'Processing...' : (project.status === 'active' ? '✓ Save Changes' : '✓ Save & Activate Project')}
              </button>
            </div>
          </div>
        )}

        {/* Show status if already activated */}
        {shouldShowDetails && project.status === 'active' && (
          <div className="card" style={{ marginTop: '2rem', background: '#d1fae5', border: '1px solid #10b981' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
              <div style={{ flex: 1 }}>
                <h3 style={{ color: '#065f46', marginBottom: '0.5rem' }}>✓ Project Active</h3>
                <p style={{ color: '#065f46', margin: 0, fontSize: '0.875rem' }}>
                  This project has been approved and is now active. All budget and metrics are finalized.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectReview;
